import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { MagnifyingGlass, MapPin, Sparkle } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import AlumniResultCard from "@/components/AlumniResultCard";

const FILTER_LABELS = {
  name: "Name", institution: "Institution", location: "Location", course: "Course",
  grad_year: "Year", batch: "Batch", profession: "Role", skills: "Skills",
};

export default function Directory() {
  const [q, setQ] = useState("");
  const [location, setLocation] = useState("");
  const [aiMode, setAiMode] = useState(false);
  const [data, setData] = useState({ total: 0, results: [] });
  const [ai, setAi] = useState(null);
  const [loading, setLoading] = useState(true);

  const search = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/alumni", { params: { q, location } });
      setData(data);
    } finally {
      setLoading(false);
    }
  }, [q, location]);

  useEffect(() => {
    if (aiMode) return;
    const t = setTimeout(search, 300);
    return () => clearTimeout(t);
  }, [search, aiMode]);

  const runAI = async () => {
    if (!q.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/ai-search", { query: q.trim() });
      setAi(data);
    } catch {
      setAi({ answer: "Search failed. Please try again.", results: [], interpreted: {} });
    } finally { setLoading(false); }
  };

  return (
    <main className="mx-auto max-w-7xl px-5 py-8">
      <p className="overline text-secondary mb-1">Find a classmate</p>
      <h1 className="font-heading text-3xl font-light tracking-tight sm:text-4xl">Alumni Directory</h1>
      <p className="mt-2 text-muted-foreground">Search {data.total} alumni across the world — privacy-first.</p>

      <div className="mt-5 flex items-center justify-between gap-3 rounded-full border border-border bg-accent/40 px-4 py-2">
        <Label htmlFor="ai-mode" className="flex items-center gap-2 text-sm font-medium">
          <Sparkle size={16} weight="fill" className="text-primary" /> Search in plain English (AI)
        </Label>
        <div className="flex items-center gap-3">
          <Link to="/ai-search" className="hidden text-xs text-primary hover:underline sm:inline">Open full AI search →</Link>
          <Switch id="ai-mode" checked={aiMode} onCheckedChange={(v) => { setAiMode(v); setAi(null); }} data-testid="directory-ai-toggle" />
        </div>
      </div>

      {aiMode ? (
        <form onSubmit={(e) => { e.preventDefault(); runAI(); }} className="mt-4 flex gap-2">
          <div className="relative flex-1">
            <Sparkle className="absolute left-4 top-1/2 -translate-y-1/2 text-primary" size={18} />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. Product managers from IIT Bombay, batch of 2015" className="rounded-full pl-11" data-testid="directory-ai-input" />
          </div>
          <Button type="submit" className="rounded-full" disabled={loading} data-testid="directory-ai-submit">{loading ? "Searching…" : "Search"}</Button>
        </form>
      ) : (
        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <MagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, headline or institution…" className="pl-11 rounded-full" data-testid="directory-search" />
          </div>
          <div className="relative sm:w-64">
            <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
            <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Location" className="pl-11 rounded-full" data-testid="directory-location" />
          </div>
        </div>
      )}

      {aiMode && ai && (
        <div className="mt-5">
          <p className="font-heading text-lg font-medium">{ai.answer}</p>
          {ai.interpreted && Object.keys(ai.interpreted).length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="text-xs uppercase tracking-wider text-muted-foreground">Understood as</span>
              {Object.entries(ai.interpreted).map(([k, v]) => (
                <Badge key={k} variant="outline" className="rounded-full font-normal">{FILTER_LABELS[k] || k}: {Array.isArray(v) ? v.join(", ") : v}</Badge>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {loading && <p className="text-muted-foreground">Searching…</p>}
        {!loading && !aiMode && data.results.length === 0 && <p className="text-muted-foreground">No alumni found. Try another search.</p>}
        {!loading && aiMode && ai && ai.results.length === 0 && <p className="text-muted-foreground">No matches yet. Try different wording.</p>}
        {!aiMode && data.results.map((p, i) => <AlumniResultCard key={p.user_id} p={p} index={i} />)}
        {aiMode && ai && ai.results.map((p, i) => <AlumniResultCard key={p.user_id} p={p} index={i} />)}
      </div>
    </main>
  );
}
